import * as React from 'react'

import apis from '@app/api/api'
import rights from '@utils/RightManager'

import Select from '@components/select/select'
import Searchbar from '@components/searchbar/searchbar'
import RegularFilters from '@components/filter/regularFilters'
import { initialFilters } from '@components/filter/filterUtils'
import Button from '@components/button/button'

import useReducer from '@store/useReducer'
import * as AuthStore from '@store/auth/auth'
import * as AccountStore from '@store/account/account'
import * as FiltersStore from '@store/filters/filters'
import * as LocalisationStore from '@store/localisation/localisation'
import useI18n from '@store/i18n/useI18n'
import styled from '@theme/styled-components'
import Theme from '@theme/Theme'

import { BiSliderAlt } from 'react-icons/bi'
import { FiChevronDown, FiChevronUp } from 'react-icons/fi'
import { MdCheckBox, MdCheckBoxOutlineBlank } from 'react-icons/md'

import { Formik } from 'formik'

interface Props {
  setBusinesses: (b: BusinessResume[]) => void
  setClusters: (clusters: Cluster[]) => void
  setIsolatedBusinesses: (isolatedBusinesses: BusinessLocation[]) => void
  setOrphans: (o: OrphanDeclarationResume[]) => void
  screenStatus: ScreenStatus
  setScreenStatus: (s: ScreenStatus) => void
  orphansVisible: boolean
  setOrphansVisible: (v: boolean) => void
  menuState: 'opened' | 'closed'
  setMenuState: (m: 'opened' | 'closed') => void
  orphansCount: number
  setNumberBusinesses: (numberBusinesses: number) => void
  currentPage: number
  setCurrentPage: (page: number) => void
}

const BusinessFilters = ({
  setBusinesses,
  setClusters,
  setIsolatedBusinesses,
  setOrphans,
  screenStatus,
  setScreenStatus,
  orphansVisible,
  setOrphansVisible,
  menuState,
  setMenuState,
  orphansCount,
  setNumberBusinesses,
  currentPage,
  setCurrentPage,
}: Props) => {
  const i18n = useI18n()

  // Barre de recherche
  const [search, setSearch] = React.useState('')

  // Choix de la région des affaires
  const auth = AuthStore.store.getState().OAuth
  const lastAuthentication = auth && auth.lastAuthentication
  const account = AccountStore.store.getState().account
  const regionsOptions: Option[] | undefined = account && account.regions
  const initialValues = React.useMemo(
    () => FiltersStore.store.getState().businessesFilters || initialFilters(regionsOptions),
    []
  )
  const southWestBound = useReducer(LocalisationStore.store, (s) => s.southWestBound)
  const northEastBound = useReducer(LocalisationStore.store, (s) => s.northEastBound)

  const fetchBusiness = (values: BusinessesFilters) => {
    FiltersStore.actions.setBusinessesFilters(values)
    setScreenStatus('loading')
    const {
      steps,
      startDate,
      endDate,
      responsable,
      company,
      region,
      statuses,
      sources,
      hasDeclarations,
      companyGroup,
    } = values
    const sourcesSelected = sources.filter((s) => !!s.value).map((s) => s.type)
    const statusesSelected = statuses.filter((s) => !!s.value).map((s) => s.type)
    const hasDeclarationsSelected = hasDeclarations.filter((s) => !!s.value).map((s) => s.type)

    if (sourcesSelected.length === 0 || statusesSelected.length === 0 || hasDeclarationsSelected.length === 0) {
      apis.orphan
        .list({
          stepIds: steps ? steps.map((s) => s.value) : [],
          startDate: startDate && startDate,
          endDate: endDate && endDate,
        })
        .then((orphansList) => {
          setBusinesses([])
          setOrphans(orphansList)
          setScreenStatus('fetched')
        })
        .catch(() => setScreenStatus('error'))
    } else {
      Promise.all([
        apis.business.map({
          latitudeA: southWestBound.lat,
          longitudeA: southWestBound.lng,
          latitudeB: northEastBound.lat,
          longitudeB: northEastBound.lng,
          search: search,
          stepIds: steps ? steps.map((s) => s.value) : [],
          startDate: startDate && startDate,
          endDate: endDate && endDate,
          responsableId: responsable ? responsable.value : undefined,
          companyId: company ? company.value : undefined,
          companyGroupId: companyGroup ? companyGroup.value : undefined,
          regions: region ? [region.value] : [],
          sources: sourcesSelected,
          statuses: statusesSelected,
          lastAuthentication,
          hasDeclarations: rights.canFilterWithWithout() ? hasDeclarationsSelected : [],
        }),
        apis.orphan.list({
          stepIds: steps ? steps.map((s) => s.value) : [],
          startDate: startDate && startDate,
          endDate: endDate && endDate,
        }),
      ])
        .then(([businessList, orphansList]) => {
          setClusters(businessList.clusters)
          setIsolatedBusinesses(businessList.businessLocations)
          setBusinesses(businessList.businessDetails)
          setOrphans(orphansList)
          setNumberBusinesses(businessList.total)
          setCurrentPage(1)
          setScreenStatus('fetched')
        })
        .catch(() => setScreenStatus('error'))
    }
  }

  const loadNextBusinesses = (values: BusinessesFilters) => {
    setScreenStatus('loading')
    const {
      steps,
      startDate,
      endDate,
      responsable,
      company,
      region,
      statuses,
      sources,
      hasDeclarations,
      companyGroup,
    } = values
    const sourcesSelected = sources.filter((s) => !!s.value).map((s) => s.type)
    const statusesSelected = statuses.filter((s) => !!s.value).map((s) => s.type)
    const hasDeclarationsSelected = hasDeclarations.filter((s) => !!s.value).map((s) => s.type)

    apis.business
      .list({
        offset: currentPage - 1,
        limit: 50,
        latitudeA: southWestBound.lat,
        longitudeA: southWestBound.lng,
        latitudeB: northEastBound.lat,
        longitudeB: northEastBound.lng,
        search: search,
        stepIds: steps ? steps.map((s) => s.value) : [],
        startDate: startDate && startDate,
        endDate: endDate && endDate,
        responsableId: responsable ? responsable.value : undefined,
        companyId: company ? company.value : undefined,
        companyGroupId: companyGroup ? companyGroup.value : undefined,
        regions: region ? [region.value] : [],
        sources: sourcesSelected,
        statuses: statusesSelected,
        lastAuthentication,
        hasDeclarations: rights.canFilterWithWithout() ? hasDeclarationsSelected : [],
      })
      .then((shortBusinessList) => {
        setBusinesses(shortBusinessList)
        setScreenStatus('fetched')
      })
      .catch(() => setScreenStatus('error'))
  }

  return (
    <Formik initialValues={initialValues} onSubmit={(values: BusinessesFilters) => fetchBusiness(values)}>
      {({ values, setFieldValue, handleSubmit }) => {
        React.useEffect(() => {
          const getBusinesses = () => fetchBusiness(values as BusinessesFilters)
          const delayedCall = setTimeout(getBusinesses, 50)
          return () => clearTimeout(delayedCall)
        }, [northEastBound])

        React.useEffect(() => {
          const getBusinesses = () => fetchBusiness(values as BusinessesFilters)
          const delayedCall = setTimeout(getBusinesses, 500)
          return () => clearTimeout(delayedCall)
        }, [search])

        React.useEffect(() => {
          loadNextBusinesses(values as BusinessesFilters)
        }, [currentPage])

        const changeAndSubmit = (fieldName: string, e: any) => {
          if (fieldName === 'company') {
            setFieldValue('companyGroup', undefined)
          } else if (fieldName === 'companyGroup') {
            setFieldValue('company', undefined)
          }
          setFieldValue(fieldName, e)
        }

        return (
          <>
            <Searchbar placeholder={i18n.t('components.menu.searchbar')} text={search} setText={(e) => setSearch(e)} />
            <ButtonContainer menuOpen={menuState === 'opened'}>
              <Container
                menuOpen={menuState === 'opened'}
                onClick={() => setMenuState(menuState === 'opened' ? 'closed' : 'opened')}>
                <BiSliderAlt
                  size={20}
                  color={menuState === 'opened' ? Theme.colors.filterSecondaryColor : Theme.colors.filterPrimarycolor}
                />
                <FilterLibelle menuOpen={menuState === 'opened'}>
                  {i18n.t('components.menu.filterButton')}
                </FilterLibelle>
                {menuState === 'opened' ? (
                  <FiChevronUp size={20} color={Theme.colors.filterSecondaryColor} />
                ) : (
                  <FiChevronDown size={20} color={Theme.colors.filterPrimarycolor} />
                )}
              </Container>
              {rights.canSeeOrphans() && (
                <LonelyPictureContainer onClick={() => setOrphansVisible(!orphansVisible)} selected={orphansVisible}>
                  <LonelyPictureLibelle selected={orphansVisible}>
                    {i18n.t('components.menu.lonelyPicture', { count: orphansCount })}
                  </LonelyPictureLibelle>
                  {orphansVisible ? (
                    <MdCheckBox color={Theme.colors.filterSecondaryColor} />
                  ) : (
                    <MdCheckBoxOutlineBlank color={Theme.colors.filterPrimarycolor} />
                  )}
                </LonelyPictureContainer>
              )}
            </ButtonContainer>

            {menuState === 'opened' && (
              <RegularFilters
                values={values}
                changeAndSubmit={changeAndSubmit}
                setScreenStatus={setScreenStatus}
                mode="home"
              />
            )}
            {regionsOptions && regionsOptions.length !== 0 && rights.canFilterOnRegion() && (
              <SelectContainer>
                <Select
                  options={regionsOptions}
                  selected={values.region}
                  setSelected={(e) => changeAndSubmit('region', e)}
                  placeholder={i18n.t('components.menu.placeholder.regions')}
                  isClearable={false}
                  noMargin
                />
              </SelectContainer>
            )}
            <FilterButtonContainer>
              <Button
                title={i18n.t('components.monitorByRegion.displayBusinesses')}
                onClick={handleSubmit}
                type="action"
                status={screenStatus === 'loading' ? 'loading' : 'active'}
              />
            </FilterButtonContainer>
          </>
        )
      }}
    </Formik>
  )
}

export default BusinessFilters

const Container = styled('div')<{ menuOpen: boolean }>`
  cursor: pointer;

  border: 1px solid ${(props) => props.theme.colors.filterPrimarycolor};
  background-color: ${(props) =>
    props.menuOpen ? props.theme.colors.filterPrimarycolor : props.theme.colors.filterSecondaryColor};
  box-sizing: border-box;
  border-radius: ${(props) => (props.menuOpen ? '17px 17px 0 0' : '24.5px')};
  ${(props) => props.menuOpen && 'border-bottom-width: 0;'}

  height: 35px;
  width: auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 4px 12px;
`
const FilterLibelle = styled('div')<{ menuOpen: boolean }>`
  margin: 0 10px;
  text-align: center;
  ${(props) => props.theme.fonts.filterLibelle}
  ${(props) => props.menuOpen && `color: ${props.theme.colors.filterSecondaryColor};`}
`
const LonelyPictureContainer = styled('div')<{ selected: boolean }>`
  cursor: pointer;
  margin-left: 25px;
  display: flex;
  flex-direction: row;
  background: ${(props) =>
    props.selected ? props.theme.colors.filterPrimarycolor : props.theme.colors.filterSecondaryColor};
  border: 1px solid
    ${(props) => (props.selected ? props.theme.colors.filterPrimarycolor : props.theme.colors.filterSecondaryColor)};
  box-sizing: border-box;
  border-radius: 24.5px;
  width: auto;
  padding: 4px 12px;
  ${(props) => !props.selected && `border: 1px solid ${props.theme.colors.filterPrimarycolor}`}
`
const LonelyPictureLibelle = styled('div')<{ selected: boolean }>`
  margin-right: 15px;
  ${(props) => props.theme.fonts.lonelyPictureLibelle}
  ${(props) => props.selected && `color: ${props.theme.colors.filterSecondaryColor};`}
`

const ButtonContainer = styled('div')<{ menuOpen: boolean }>`
  display: flex;
  margin: 20px 0;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  ${(props) => props.menuOpen && 'margin-bottom: 0;'}
`
const SelectContainer = styled('div')`
  padding: 10px 0;
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  border-top-width: 1px;
`
const FilterButtonContainer = styled('div')`
  display: flex;
  padding: 10px 0px;
  justify-content: center;
  align-items: center;
`
